import { navigate } from 'gatsby';
import { useEffect, useRef, useState } from 'react';
import twMerge from 'twMerge';

import Checkbox from 'molecules/Checkbox';
import Dropdown from 'molecules/Dropdown';
import Icon from 'molecules/Icon';

import SubscribeForm from 'components/Resources/SubscribeForm';
import sidebarStyles, { sidebarStickyStyles } from 'components/Resources/styles/Sidebar.styles';

import { createSlugFromName } from 'utils/createSlugFromName';

import type { ContentfulTaxonomyTag } from 'graphqlTypes';
import type { FC } from 'react';

export type SidebarProps = {
  activeTag?: ContentfulTaxonomyTag;
  tags: ContentfulTaxonomyTag[];
  resourceType: string;
};

const dropdownItems = [{ label: 'All' }, { label: 'Janitorial' }, { label: 'Landscape' }, { label: 'Cleaning' }];

const Sidebar: FC<SidebarProps> = ({ activeTag, tags, resourceType }) => {
  const scrollContainerRef = useRef(null);
  const [showTopFade, setShowTopFade] = useState(false);
  const [showBottomFade, setShowBottomFade] = useState(true);

  const ClearFilter = 'div';

  const handleScroll = () => {
    const container = scrollContainerRef.current;

    if (container) {
      setShowTopFade(container.scrollTop > 0);
      setShowBottomFade(container.scrollTop + container.clientHeight < container.scrollHeight - 1);
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;

    if (container) {
      handleScroll();
      container.addEventListener('scroll', handleScroll);

      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, []);

  let industryItems = dropdownItems.filter(x => x.label === 'All' || tags.some(tag => tag.name === x.label));
  tags = tags?.filter(tag => !dropdownItems.some(di => di.label === tag.name));

  if (resourceType === 'blog') {
    industryItems = industryItems.filter(item => item.label !== 'Janitorial');
  }

  const dropdownDefaultValue = industryItems.findIndex(item => item.label === activeTag?.name) || 0;

  return (
    <div className={twMerge(sidebarStyles())}>
      <div className={twMerge(sidebarStickyStyles())}>
        <p className="text-xl font-semibold text-black">Categories</p>
        {/* Temporarily disabling search until listing is developed */}
        {/* <TextField
          placeholder="Search"
          icon="search"
          defaultValue={query}
          onChange={e => handleInputChange(e.target.value)}
        /> */}
        <Dropdown
          label="Select Industry"
          items={industryItems}
          type="filter"
          defaultValue={dropdownDefaultValue}
          onChange={e => {
            if (e === 0) {
              navigate(`/${resourceType}#listing`);
            } else {
              navigate(`/${resourceType}/${industryItems[e]?.label?.toLowerCase()?.replace(/\s+/g, '-')}#listing`);
            }
          }}
        />
        <div className="relative block lg:overflow-y-auto">
          {showTopFade && <div className="absolute block h-[40px] w-full bg-gradient-to-b from-white to-transparent" />}
          {tags?.length > 0 && (
            <div
              className={twMerge(
                'grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-1 lg:overflow-y-scroll h-full',
              )}
              style={{ scrollbarWidth: 'thin' }}
              ref={scrollContainerRef}
            >
              {tags?.map(tag => (
                <Checkbox
                  key={tag?.name}
                  label={tag?.name || ''}
                  handleClick={() => {
                    const tagName = createSlugFromName(tag?.name || '');
                    if (window.location.pathname === `/${resourceType}/${tagName}`) {
                      navigate(`/${resourceType}#listing`);
                    } else {
                      navigate(`/${resourceType}/${tagName}#listing`);
                    }
                  }}
                  checked={tag?.name === activeTag?.name}
                />
              ))}
            </div>
          )}
          {showBottomFade && (
            <div className="absolute bottom-0 hidden h-[40px] w-full bg-gradient-to-b from-transparent to-white lg:block" />
          )}
        </div>
        {activeTag && (
          <ClearFilter
            className="flex cursor-pointer items-center gap-2 p-2 text-sm font-semibold text-green-500"
            onClick={() => navigate(`/${resourceType}#listing`)}
          >
            <Icon icon="close-green-bg" size={20} aria-hidden={true} className="fill-transparent" />
            Clear all filters
          </ClearFilter>
        )}
        <SubscribeForm
          formId="03c161b8-e171-4e19-9159-434e89791c99"
          heading="Sign up for our newsletter"
          subhead="Get the latests articles, delivered right to your inbox."
        />
      </div>
    </div>
  );
};

export default Sidebar;
